import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import MainLayout from "../../../components/Layout/MainLayout";
import Loader from "../../../components/Common/Loader";
import { BackIcon } from "../../../components/Dashboard/DataCenterIcons";
import { commonService } from "../../../services/commonService";
import { ROUTES } from "../../../utils/constants";

interface HeaderField {
    id: number;
    header_name: string;
}

// Same restriction as the pc-counts list page's Edit icon - pc (17),
// pc_manager (16), and admin (1) only. This page is reachable directly by
// URL, so it needs its own guard rather than relying on the list page
// having disabled the button.
const CAN_EDIT_ROLE_IDS = ['1', '16', '17'];

const EditRecordPage: React.FC = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const recordId = searchParams.get("id");

    useEffect(() => {
        if (!CAN_EDIT_ROLE_IDS.includes(localStorage.getItem('role_id') || '')) {
            navigate(ROUTES.CD, { replace: true });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const [headers, setHeaders] = useState<HeaderField[]>([]);
    const [formData, setFormData] = useState<Record<number, string>>({});
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [message, setMessage] = useState<{
        type: "success" | "error";
        text: string;
    } | null>(null);

    const [contactCountId, setContactCountId] = useState<number | null>(null);

    useEffect(() => {
        if (recordId) {
            fetchHeadersAndRecord();
        }
    }, [recordId]);

    const fetchHeadersAndRecord = async () => {
        try {
            setLoading(true);

            // Fetch headers
            const headerRes = await commonService.cdget();
            const activeHeaders =
                (headerRes?.data || [])
                    .filter((item: any) => !item.is_deleted)
                    .reverse()
                    .map((item: any) => ({
                        id: item.id,
                        header_name: item.header_name
                    }));

            setHeaders(activeHeaders);

            // Find Contact Counts ID
            activeHeaders.forEach((h: any) => {
                if (h.header_name === "Contact Counts") {
                    setContactCountId(h.id);
                }
            });

            // Fetch record
            const recordRes = await commonService.recordGetById(recordId);
            const existingData = recordRes.data.row_data || {};

            // Convert string keys → number keys
            const formattedData: Record<number, string> = {};
            activeHeaders.forEach((h: HeaderField) => {
                formattedData[h.id] = existingData[h.id] || "";
            });

            setFormData(formattedData);

        } catch (error) {
            setMessage({
                type: "error",
                text: "Failed to load record."
            });
        } finally {
            setLoading(false);
        }
    };

    const handleChange = (id: number, value: string) => {
        setFormData(prev => ({
            ...prev,
            [id]: value
        }));
    };

    const handleSubmit = async () => {
        setMessage(null);

        if (
            contactCountId &&
            (!formData[contactCountId] ||
                formData[contactCountId].trim() === "")
        ) {
            setMessage({
                type: "error",
                text: "Contact Counts is required."
            });
            return;
        }

        try {
            setSubmitting(true);

            await commonService.recordUpdate(recordId, {
                row_data: formData
            });

            setMessage({
                type: "success",
                text: "Record updated successfully."
            });

            setTimeout(() => {
                navigate(-1);
            }, 1000);

        } catch (error: any) {
            setMessage({
                type: "error",
                text:
                    error?.response?.data?.message ||
                    "Failed to update record."
            });
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <MainLayout>
            <div className="pc-counts-page custom-scrollbar">
                <main className="pc-counts-main">

                    <div className="pc-counts-header">
                        <button
                            onClick={() => navigate(-1)}
                            className="pc-counts-back-button"
                        >
                            <BackIcon />
                            Back
                        </button>
                    </div>

                    <div className="pc-counts-dashboard">
                        <div className="pc-counts-dashboard-header">
                            <h2 className="pc-counts-dashboard-title">
                                Edit Record
                            </h2>
                        </div>

                        {loading ? (
                            <Loader text="Loading record..." />
                        ) : (
                            <div style={{
                                background: "#fff",
                                padding: "30px",
                                borderRadius: "12px",
                                boxShadow: "0 6px 18px rgba(0,0,0,0.08)",
                                maxWidth: "1000px",
                                margin: "0 auto"
                            }}>

                                <div style={{
                                    display: "grid",
                                    gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))",
                                    gap: "20px",
                                    marginBottom: "25px"
                                }}>
                                    {headers.map(header => {
                                        const isRequired =
                                            header.id === contactCountId;

                                        return (
                                            <div key={header.id}>
                                                <label style={{
                                                    display: "block",
                                                    marginBottom: "6px",
                                                    fontWeight: 600,
                                                    fontSize: "14px"
                                                }}>
                                                    {header.header_name}
                                                    {isRequired && (
                                                        <span style={{ color: "red" }}> *</span>
                                                    )}
                                                </label>

                                                <input
                                                    type="text"
                                                    value={formData[header.id] || ""}
                                                    onChange={(e) =>
                                                        handleChange(header.id, e.target.value)
                                                    }
                                                    style={{
                                                        width: "100%",
                                                        padding: "10px",
                                                        borderRadius: "8px",
                                                        border: "1px solid #dcdcdc"
                                                    }}
                                                />
                                            </div>
                                        );
                                    })}
                                </div>

                                {message && (
                                    <div style={{
                                        padding: "12px",
                                        borderRadius: "8px",
                                        marginBottom: "20px",
                                        background:
                                            message.type === "success"
                                                ? "#e6f7ed"
                                                : "#fdecea",
                                        color:
                                            message.type === "success"
                                                ? "#1e7e34"
                                                : "#c62828"
                                    }}>
                                        {message.text}
                                    </div>
                                )}

                                <div style={{ textAlign: "right" }}>
                                    <button
                                        onClick={handleSubmit}
                                        disabled={submitting}
                                        style={{
                                            background: "#61A1D4",
                                            color: "#fff",
                                            border: "none",
                                            padding: "10px 24px",
                                            borderRadius: "8px",
                                            fontWeight: 600
                                        }}
                                    >
                                        {submitting ? "Updating..." : "Update Record"}
                                    </button>
                                </div>

                            </div>
                        )}
                    </div>
                </main>
            </div>
        </MainLayout>
    );
};

export default EditRecordPage;